import { type FC, useEffect, useState } from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import { type UserOrderResponseWithFlags } from '@redteclab/api/clients/bully'

import { CircularProgress } from 'base-ui'

import { useOpenableControls } from '../../generic-hooks'
import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../global-config'
import {
  HTTP_STATUS_CODE_FORBIDDEN,
  HTTP_STATUS_CODE_UNAUTHORIZED,
} from '../../http'
import { LoginRegistrationUserLoginModal } from '../../login-registration'
import {
  refetchUseApiOrdersSwrGetOrders,
  useApiOrdersSwrGetOrders,
} from '../../order'
import { TENANT } from '../../tenant'
import {
  urlResolverGetAccountOrders,
  urlResolverGetOffersCategoryPage,
} from '../../url-handling'
import { useScrollToIdIntoViewWhenHashInUrlAfterLoading } from '../hooks/useScrollToIdIntoViewWhenHashInUrlAfterLoading'

import { AccountManagementDialog } from './AccountManagementDialog/AccountManagementDialog'
import { AccountManagementDialogProvider } from './AccountManagementDialog/AccountManagementDialogContext'
import { ContactPageOrdersSectionContent } from './ContactPageOrdersSectionContent'

const getOrderSectionData = (
  config: {
    globalConfig: PublicGlobalConfig
    intl: IntlShape
    isUserLoggedIn: boolean
  },
  orders?: UserOrderResponseWithFlags[],
): { ctaId: string; ctaLabel: string; description: string; href?: string } => {
  const { globalConfig, intl, isUserLoggedIn } = config
  const viewAllOrdersLabel = intl.formatMessage({
    id: 'contact.ordersSection.cta.label',
  })

  const logInLabel = intl.formatMessage({ id: 'general.label.subscribe' })
  const findProductsLabel = intl.formatMessage({
    id: 'contact.ordersSection.cta.findProductsLabel',
  })

  const descriptionLabelSecondVersion = intl.formatMessage({
    id: 'contact.ordersSection.description.secondVersion',
  })

  const descriptionLabel = intl.formatMessage({
    id: 'helpCenter.ordersSection.description.v1',
  })

  const ordersSectionLabel =
    globalConfig.tenant === TENANT.COM
      ? descriptionLabel
      : descriptionLabelSecondVersion

  if (!isUserLoggedIn) {
    return {
      ctaId: 'orders-section-cta-login',
      ctaLabel: logInLabel,
      description: ordersSectionLabel,
      href: undefined,
    }
  }

  if (orders?.length === 0) {
    return {
      ctaId: 'orders-section-cta-offers',
      ctaLabel: findProductsLabel,
      description: descriptionLabel,
      href: urlResolverGetOffersCategoryPage(globalConfig),
    }
  }

  return {
    ctaId: 'orders-section-cta-orders',
    ctaLabel: viewAllOrdersLabel,
    description: ordersSectionLabel,
    href: urlResolverGetAccountOrders(globalConfig),
  }
}

export const ContactPageOrdersSection: FC = () => {
  const { data, error, isLoading } = useApiOrdersSwrGetOrders({
    itemsPerPage: 6,
  })
  const globalConfig = useGlobalConfigContext()
  const intl = useIntl()
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false)
  const userModalControls = useOpenableControls()
  const accountManagementModalControls = useOpenableControls()

  useEffect(() => {
    if (!error && !isLoading) {
      setIsUserLoggedIn(true)
    }
  }, [error, isLoading])

  useScrollToIdIntoViewWhenHashInUrlAfterLoading(
    '#direct-contact',
    '#direct-contact-section',
    isLoading,
  )

  const orderSectionData = getOrderSectionData(
    {
      globalConfig,
      intl,
      isUserLoggedIn,
    },
    data?.orders,
  )

  if (
    error &&
    error.status !== HTTP_STATUS_CODE_UNAUTHORIZED &&
    error.status !== HTTP_STATUS_CODE_FORBIDDEN
  ) {
    return null
  }

  return (
    <div className="w-full bg-light-brand-low px-4 py-9 tablet:py-12 tablet:text-center">
      <h1 className="mb-4 text-2xl font-medium">
        <FormattedMessage id="contact.ordersSection.title.v2" />
      </h1>
      {!isLoading && (
        <p className="text-l text-dark-primary-strong tablet:mb-12">
          {orderSectionData.description}
        </p>
      )}
      <div className="flex w-full justify-center">
        {isLoading ? (
          <CircularProgress className="shrink-0 py-16 text-3xl text-dark-brand" />
        ) : (
          <ContactPageOrdersSectionContent
            handleAccountManagementModalOpen={() => {
              accountManagementModalControls.handleOpen()
            }}
            handleLoginModalOpen={() => {
              userModalControls.handleOpen()
            }}
            isUserLoggedIn={isUserLoggedIn}
            orders={data?.orders}
            ordersSectionData={orderSectionData}
          />
        )}
      </div>
      <AccountManagementDialogProvider
        dialogControls={accountManagementModalControls}
      >
        <AccountManagementDialog />
      </AccountManagementDialogProvider>

      <LoginRegistrationUserLoginModal
        afterLoggedIn={() => {
          void refetchUseApiOrdersSwrGetOrders()
          userModalControls.handleClose()
        }}
        onClose={userModalControls.handleClose}
        open={userModalControls.isOpen}
      />
    </div>
  )
}
